import { Button } from 'antd';
import Page from 'components/Page';
import Table from 'components/Table';
import { useMutation } from 'hooks/apollo';
import useDataSource from 'hooks/useDataSource';
import React, { useState } from 'react';
import { controlColumns } from './columns';
import { useAccessibilityReview } from './context';
import { CriterionIssuesModal } from './Evidence';
import { ACCEPT_ACCESSIBILITY_REVIEW_COVERAGE } from './graphql';
import { ManualReviewModal } from './ManualReview';
import { DetailField, DetailFields } from 'components/DetailFields';
import { InactiveNotice, PageIntroduction, ReviewPage } from './ReviewPage';

const CHECKLIST_TABLE_SCROLL = { x: 860 };

const Checklist = () => {
  const { refetch, result, scope, shellLoading, loading: resultLoading } = useAccessibilityReview();
  const { dataSource } = useDataSource();
  const [acceptCoverage, { loading: acceptingCoverage }] = useMutation(ACCEPT_ACCESSIBILITY_REVIEW_COVERAGE, { onCompleted: () => refetch() });
  const [selectedCriterion, setSelectedCriterion] = useState(null);
  const [selectedManualControl, setSelectedManualControl] = useState(null);
  const suggestedUnits = result?.reviewUnits?.filter(({ status }) => status === 'suggested') || [];
  const acceptedUnits = result?.reviewUnits?.filter(({ status }) => status === 'accepted') || [];
  const reviewCoverageAccepted = Boolean(result?.reviewUnits?.some(({ status }) => status === 'accepted'));

  return (
    <ReviewPage title="WCAG checklist">
      <Page.Content>
        {!shellLoading && !scope && <InactiveNotice />}
        <PageIntroduction title="How to use this checklist">
          Each criterion shows its evidence status, what automation can contribute, and the manual check needed for a complete assessment. Page types are potential applicability; the reviewer confirms whether each criterion applies to the selected HTML page or PDF.
        </PageIntroduction>
        {scope && suggestedUnits.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            <h3 style={{ margin: '0 0 6px' }}>Suggested manual review coverage</h3>
            <div style={{ marginBottom: 8 }}>
              Extellio has suggested
              {' '}
              {suggestedUnits.length}
              {' '}
              review units from the URL structure. Each unit has one representative page; you can accept this proposal now and start reviewing, or refine it later.
            </div>
            <Button type="primary" loading={acceptingCoverage} onClick={() => acceptCoverage({ variables: { dataSourceId: dataSource.id } })}>{reviewCoverageAccepted ? 'Update review coverage' : 'Use suggested coverage'}</Button>
          </div>
        )}
        {scope && reviewCoverageAccepted && (
          <div style={{ marginBottom: 16 }}>
            <h3 style={{ margin: '0 0 6px' }}>Manual review coverage</h3>
            <div>
              {acceptedUnits.length}
              {' '}
              accepted review units define the representative pages used for the manual checklist.
            </div>
          </div>
        )}
        <DetailFields style={{ marginBottom: 16 }}>
          <DetailField label="WCAG criteria — automated issue found">{result?.summary?.automatedFailureControlCount || 0}</DetailField>
          <DetailField label="WCAG criteria — manual check required">{result?.summary?.manualReviewControlCount || 0}</DetailField>
          <DetailField label="WCAG criteria — no evidence yet">{result?.summary?.notStartedControlCount || 0}</DetailField>
        </DetailFields>
        <Table
          columns={controlColumns({
            onReviewManually: setSelectedManualControl,
            onViewIssues: setSelectedCriterion,
            reviewCoverageAccepted,
          })}
          dataSource={result?.controls || []}
          loading={resultLoading}
          pagination={{ pageSize: 25 }}
          resizable={false}
          interactive={false}
          scroll={CHECKLIST_TABLE_SCROLL}
          tableLayout="fixed"
        />
      </Page.Content>
      <CriterionIssuesModal criterion={selectedCriterion} onClose={() => setSelectedCriterion(null)} />
      <ManualReviewModal control={selectedManualControl} onClose={() => setSelectedManualControl(null)} />
    </ReviewPage>
  );
};

export default Checklist;
